import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { loginAPI } from '@/providers/authApi'
import { zodResolver } from "@hookform/resolvers/zod"
import { useRouter } from '@tanstack/react-router'
import { AnimatePresence, motion } from "framer-motion"
import { ChevronRight, Eye, EyeOff, Store } from 'lucide-react'
import { useState } from 'react'
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"


// Validation schemas
const emailSchema = z.object({
  username: z.string().min(1, "Username is required").default(
    localStorage.getItem('rememberMeUsername') || ''
  ),
  rememberMe: z.boolean().default(false)
})

const passwordSchema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters"),
  username: z.string(),
  rememberMe: z.boolean().default(false)
})

type EmailFormData = z.infer<typeof emailSchema>
type PasswordFormData = z.infer<typeof passwordSchema>


const Index = () => {
  const [step, setStep] = useState<'email' | 'password'>('email')
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()
  const [showPassword, setShowPassword] = useState(false)
  
  const signIn = async (data: PasswordFormData) => {
    try {
      const user = await loginAPI(data.username, data.password)
      
      if (!user) {
        throw new Error("No response from server")
      }

      // Store tokens in localStorage
      if (user.accessToken) {
        localStorage.setItem('accessToken', user.accessToken)
      }
      if (user.refreshToken) {
        localStorage.setItem('refreshToken', user.refreshToken)
      }

      // Store user data
      localStorage.setItem('user', JSON.stringify(user))

      setIsLoading(false)

      if (data.rememberMe) {
        localStorage.setItem('rememberMeUsername', data.username)
      }

      // Role-based routing
      const userRole = user.role?.toLowerCase()
      
      if (userRole === 'admin') {
        router.navigate({
          to: "/dashboard"
        })
      } else if (userRole === 'manager') {
        router.navigate({
          to: "/manager/dashboard"
        })
      } else if (userRole === 'technician') {
        router.navigate({
          to: "/technician"
        })
      } else {
        // Default fallback to home
        router.navigate({
          to: "/home"
        })
      }
    } catch (error: any) {
      console.error("Login error:", error)
      setIsLoading(false)
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred. Please try again later.'
      toast.error(errorMessage)
      throw error
    }
  }
  // Email form
  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      username: localStorage.getItem('rememberMeUsername') || '',
      rememberMe: false
    }
  })

  // Password form
  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      username: email
    }
  })

  const onEmailSubmit = async (data: EmailFormData) => {
    try {
      setIsLoading(true)
      setEmail(data.username)
      setStep('password')
      passwordForm.setValue('username', data.username)
      passwordForm.setValue('rememberMe', data.rememberMe)
    } catch (error) {
      toast.error('Invalid email address')
    } finally {
      setIsLoading(false)
    }
  }

  const onPasswordSubmit = async (data: PasswordFormData) => {
    try {
      setIsLoading(true)
      await signIn(data)
      toast.success('Successfully signed in')
    } catch (error: any) {
      console.error("Password submit error:", error)
      // Set form error
      passwordForm.setError('password', {
        type: 'manual',
        message: error?.response?.data?.message || error?.message || 'Invalid credentials'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-gradient-overlay vector-overlay">
      
      <div className="relative z-10 flex flex-col items-center gap-8">
        <AnimatePresence mode="wait">
          {step === 'email' ? (
            <motion.div
              key="email-logo"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <img src="/logo.svg" alt="Logo" className="w-12 h-auto filter" />
            </motion.div>
          ) : (
            <motion.div
              key="password-logos"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-4"
            >
              <img src="/logo.svg" alt="Logo" className="w-12 h-auto" />
              <div className="flex items-center gap-[-8px]">
                <ChevronRight className="w-6 h-6 text-white" />
                <ChevronRight className="w-6 h-6 text-white -ml-4" />
                <ChevronRight className="w-6 h-6 text-white -ml-4" />
              </div>
              <div className="w-12 h-12 rounded-full bg-[#2C2C35] flex items-center justify-center">
                <Store className="w-6 h-6 text-white" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence mode="wait">
          {step === 'email' ? (
            <motion.div
              key="email"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
            >
              <Card className="w-[400px] h-[320px] bg-[#1C1C24] border-0">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-white text-center">Sign in to your account</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Form {...emailForm}>
                    <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-6">
                      <FormField
                        control={emailForm.control}
                        name="username"
                        render={({ field }) => (
                          <FormItem>
                            <Label className="text-sm text-gray-300">Username</Label>
                            <FormControl>
                              <Input 
                                {...field}
                                className="bg-[#2C2C35] border-gray-700 text-white focus:border-[#474DFF] focus:ring-[#474DFF]"
                                placeholder="Enter your username"
                              />
                            </FormControl>
                            <FormMessage className="text-red-500" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={emailForm.control}
                        name="rememberMe"
                        render={({ field }) => (
                          <FormItem className="flex gap-2 items-center">
                            <FormControl>
                              <input 
                                type="checkbox" 
                                checked={field.value}
                                onChange={field.onChange}
                                className="rounded bg-[#2C2C35] border-gray-700 text-[#474DFF] focus:ring-[#474DFF] mt-1"
                              />
                            </FormControl>
                            <Label className="text-sm text-gray-300">
                              Remember me
                            </Label>
                          </FormItem>
                        )}
                      />  

                      <Button 
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#09090B] text-white hover:bg-[#09090B]/90"
                      >
                        {isLoading ? 'Loading...' : 'Continue'}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              key="password"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
            >
              <div className="flex items-center justify-between mb-4 w-[400px]">
                <button 
                  onClick={() => setStep('email')}
                  className="text-sm text-gray-300 hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-arrow-left"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
                  Back
                </button>
              </div>
              <Card className="w-[400px] h-[320px] bg-[#1C1C24] border-0">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-white text-center">Enter your password</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Form {...passwordForm}>
                    <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between bg-[#2C2C35] p-3 rounded-md">
                          <span className="text-sm text-gray-300">{email}</span>
                        </div>
                        <FormField
                          control={passwordForm.control}
                          name="password"
                          render={({ field }) => (
                            <FormItem>
                              <Label className="text-sm text-gray-300">Password</Label>
                              <FormControl>
                                <div className="relative">
                                  <Input 
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    className="bg-[#2C2C35] border-gray-700 text-white focus:border-[#474DFF] focus:ring-[#474DFF] pr-10"
                                    placeholder="Enter your password"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                  >
                                    {showPassword ? (
                                      <EyeOff className="w-4 h-4" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-500" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <Button 
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#09090B] text-white hover:bg-[#09090B]/90"
                      >
                        {isLoading ? 'Loading...' : 'Sign in'}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
              <div className="flex justify-center w-[400px] mt-0">
                <Button
                  type="button"
                  variant="link"
                  className="text-gray-300 underline hover:text-white px-0"
                  onClick={() => router.navigate({ to: "/forgot-password" })}
                >
                  Did you forget your password?
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Index